import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Phone, Mail, Send, Loader2 } from "lucide-react";

const ContactModal = ({ onClose, apiUrl }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${apiUrl}/contact`, {
        name,
        email,
        phone,
        message
      });
      toast.success("Message sent! We'll get back to you soon.");
      onClose();
    } catch (error) {
      toast.error("Error sending message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative glass rounded-lg p-8 max-w-lg w-full animate-fade-in" data-testid="contact-modal">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
          data-testid="close-contact-modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="font-serif text-3xl text-white mb-2">Contact Us</h2>
          <p className="text-white/50 text-sm">We'd love to hear from you</p>
        </div>

        {/* Direct Contact Info */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 p-4 glass-light rounded-lg">
          <a 
            href="tel:6823228644"
            className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>(682) 322-8644</span>
          </a>
          <span className="hidden sm:block text-white/20">|</span>
          <a 
            href="mailto:reservationsluxury@ivarge.com"
            className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors text-sm"
          >
            <Mail className="w-4 h-4" />
            <span>reservationsluxury@ivarge.com</span>
          </a>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/30 text-xs uppercase tracking-widest">Or send a message</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Phone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone"
                className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="contact-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="contact-email"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Message *</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help you?"
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#D4AF37]/50"
              data-testid="contact-message"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full flex items-center justify-center gap-2"
            data-testid="send-message-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
